import { moderationApproveApi, moderationRejectApi, storeModerationApi } from '@apis/cp/sellers';
import { IconVerifiedUser } from '@common-web/icons';
import { Actions, Status, Table, Toast } from '@common-web/index';
import TextWithIcon from '@common-web/text-with-icon';
import Tooltip from '@common-web/tooltip';
import { handleFilterChips } from '@components-web/filters/helper';
import { Filters } from '@components-web/index';
import { UPLOADS_MODERATION_PATH } from '@constants/cp-paths';
import { DATE_FORMATS } from '@constants/generic-constants';
import {
  ACTION_APPROVE_MODERATION,
  ACTION_REJECT_MODERATION,
  PERMISSION_KEY_STORE,
} from '@constants/permission-constants';
import { strings } from '@constants/strings';
import { getErrorString, getQueryParams } from '@utils/utils';
import { Flex, Image, Modal } from 'antd';
import PermissionsHelper from 'auth/helper/PermissionHelper';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { storeUploadsTableColumns, UPLOADS_MODERATION_FILTERS_LIST } from './store-uploads-constants';

const StoreUploads = () => {
  const location = useLocation();

  const [statusModal, setStatusModal] = useState({ visible: false, id: null, action: '' });
  const [confirmLoading, setConfirmLoading] = useState();
  const [filterChips, setFilterChips] = useState([]);
  const [loading, setLoading] = useState(false);
  const [storeData, setStoreData] = useState([]);

  const filtersList = UPLOADS_MODERATION_FILTERS_LIST;

  useEffect(() => {
    setFilterChips(handleFilterChips({ location, filtersList }));
  }, [location?.search]);

  const getStores = async (params) => {
    setLoading(true);
    try {
      const updatedParams = params && params?.replace('?', '');
      const res = await storeModerationApi(updatedParams);
      if (res?.success) {
        setStoreData(res.stores || []);
      }
    } catch (err) {
      Toast.error(getErrorString(err));
    }
    setLoading(false);
  };

  const handleStatusChange = async () => {
    const { id, action } = statusModal;
    setConfirmLoading(true);

    try {
      const response = action === 'approve' ? await moderationApproveApi(id) : await moderationRejectApi(id);
      Toast.success(response?.message || 'Status updated');
      if (response?.success) {
        refetchListing();
      }
    } catch (err) {
      Toast.error(getErrorString(err));
    } finally {
      setConfirmLoading(false);
      setStatusModal({ visible: false, id: null, action: '' });
    }
  };

  const refetchListing = () => {
    setTimeout(async () => {
      const params = getQueryParams({ location });
      await getStores(params);
    }, 1000);
  };

  const isExactlyOneImage = (store) => {
    const { logo, banners, mobile_banners } = store?.moderation_data || {};

    const logoCount = logo ? 1 : 0;
    const bannersCount = Array.isArray(banners) ? banners.length : 0;
    const mobileBannersCount = Array.isArray(mobile_banners) ? mobile_banners.length : 0;

    const totalCount = logoCount + bannersCount + mobileBannersCount;

    return totalCount === 1;
  };

  const getTableData = () => {
    return storeData.map((store) => {
      const { id, name, owner, city, on_vacation, vacation_end_date, store_type } = store;
      const { logo, banners, mobile_banners } = store?.moderation_data || {};

      const hasAnyImage = !!(
        logo ||
        (Array.isArray(banners) && banners.length > 0) ||
        (Array.isArray(mobile_banners) && mobile_banners.length > 0)
      );

      return {
        key: id,
        [strings.logo]: <Image src={logo?.sizes?.large || logo?.url} height={'100%'} preview />,
        [strings.banner]: <Image src={banners?.[0]?.sizes?.large || banners?.url} height={'100%'} preview />,
        [strings.mobile_banner]: (
          <Image src={mobile_banners?.[0]?.sizes?.large || mobile_banners?.url} height={'100%'} preview />
        ),
        [strings.store_details]: (
          <Flex vertical gap={2}>
            <Flex gap={5}>
              <div className="fs-12">
                <span className="gray-500">{strings.seller_id}</span>{' '}
                <span style={{ color: 'var(--ant-color-primary)' }}>{id}</span>
              </div>
              {store_type === 'test' && (
                <Status
                  style={{
                    '--ant-tag-default-bg': '#FFE48D',
                    '--ant-tag-default-color': '#7a5409',
                  }}
                  name={strings.test}
                  className={'fw-600'}
                  size="sm"
                />
              )}
              {on_vacation && (
                <Tooltip
                  title={
                    vacation_end_date ? `Till ${dayjs(vacation_end_date).format(DATE_FORMATS.dayMonthYear)}` : null
                  }
                >
                  <Status
                    style={{
                      '--ant-tag-default-bg': '#FFE48D',
                      '--ant-tag-default-color': '#7a5409',
                    }}
                    name={strings.on_vacation}
                    className={'fw-600'}
                    size="sm"
                  />
                </Tooltip>
              )}
            </Flex>
            <Flex align={'center'} gap={4}>
              <span className="fw-600">{name}</span>
              {owner?.is_verified && <IconVerifiedUser color="var(--ant-color-secondary)" size="1.2em" />}
            </Flex>
            <TextWithIcon icon={'IoLocationSharp'} text={city?.title || '-'} />
          </Flex>
        ),
        [strings.actions]: hasAnyImage ? (
          <Actions
            actions={[
              {
                value: 'Approve',
                icon: 'MdCheckCircle',
                show: PermissionsHelper.getPermissions(PERMISSION_KEY_STORE, ACTION_APPROVE_MODERATION),
                onClick: () =>
                  setStatusModal({ visible: true, id, action: 'approve', isSingleImage: isExactlyOneImage(store) }),
              },
              {
                value: 'Reject',
                icon: 'MdCancel',
                show: PermissionsHelper.getPermissions(PERMISSION_KEY_STORE, ACTION_REJECT_MODERATION),
                onClick: () =>
                  setStatusModal({ visible: true, id, action: 'reject', isSingleImage: isExactlyOneImage(store) }),
              },
            ]}
          />
        ) : (
          ''
        ),
      };
    });
  };

  return (
    <>
      <Filters
        filtersList={filtersList}
        fetch={getStores}
        rootPath={UPLOADS_MODERATION_PATH}
        filterChips={filterChips}
        showManageSearchSkeleton={false}
        shouldPaginate={false}
      />
      <Table columns={storeUploadsTableColumns()} dataSource={getTableData()} loading={loading} pagination={false} />

      <Modal
        open={statusModal.visible}
        title={`${statusModal.action === 'approve' ? 'Approve' : 'Reject'} ${
          statusModal.isSingleImage ? 'Image' : 'Images'
        }`}
        onOk={handleStatusChange}
        onCancel={() => setStatusModal({ visible: false, id: null, action: '', isSingleImage: false })}
        okText="Confirm"
        cancelText="Cancel"
        okButtonProps={{ loading: confirmLoading }}
      >
        Are you sure you want to {statusModal.action} {statusModal.isSingleImage ? 'this image' : 'these images'}?
      </Modal>
    </>
  );
};

export default StoreUploads;
